import { useCallback, useState } from 'react';
import { useApi, API_BASE_URL } from './useApi';

export interface AccountAttorney {
  id: string;
  barNumber: string;
  barState: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  firmName?: string;
  email?: string;
  phone?: string;
}

interface AttorneyListResponse {
  data: AccountAttorney[];
}

interface AttorneyLookupResponse {
  data: Array<{
    firstName?: string;
    lastName?: string;
    middleName?: string;
    firmName?: string;
    email?: string;
    phone?: string;
  }>;
}

interface AttorneyFormData {
  barNumber: string;
  barState: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  firmName?: string;
  email?: string;
  phone?: string;
}

interface UseAttorneyApiReturn {
  getAttorneys: () => Promise<AccountAttorney[]>;
  createAttorney: (data: AttorneyFormData) => Promise<AccountAttorney>;
  updateAttorney: (id: string, data: AttorneyFormData) => Promise<AccountAttorney>;
  deleteAttorney: (id: string) => Promise<void>;
  lookupAttorney: (barNumber: string) => Promise<AttorneyLookupResponse>;
  loading: boolean;
  error: string | null;
}

export function useAttorneyApi(): UseAttorneyApiReturn {
  const { get, post, del, fetchWithAuth } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAttorneys = useCallback(async (): Promise<AccountAttorney[]> => {
    setLoading(true);
    setError(null);
    try {
      const result = await get<AttorneyListResponse>(`${API_BASE_URL}/api/efiling/attorneys`);
      return result.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [get]);

  const createAttorney = useCallback(async (data: AttorneyFormData): Promise<AccountAttorney> => {
    setLoading(true);
    setError(null);
    try {
      const result = await post<AccountAttorney>(`${API_BASE_URL}/api/efiling/attorneys`, data);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [post]);

  const updateAttorney = useCallback(async (id: string, data: AttorneyFormData): Promise<AccountAttorney> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/efiling/attorneys/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.message || err.error || 'Request failed');
      }
      return response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const deleteAttorney = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await del(`${API_BASE_URL}/api/efiling/attorneys/${encodeURIComponent(id)}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [del]);

  const lookupAttorney = useCallback(async (barNumber: string): Promise<AttorneyLookupResponse> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ bar: barNumber });
      const result = await get<AttorneyLookupResponse>(`${API_BASE_URL}/api/efiling/attorneys/lookup?${params}`);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [get]);

  return {
    getAttorneys,
    createAttorney,
    updateAttorney,
    deleteAttorney,
    lookupAttorney,
    loading,
    error,
  };
}
